<template>
    <flex-element
        :element-type="elementType"
        tag="article"
    >
        <div>
            <v-calendar
                ref="calendar"
                :attributes="attributes"
                :theme="theme"
                :title-position="titleAlignment"
                @dayclick="dayClicked"
            />
            <input
                :id="facetPropertyName"
                ref="hiddenSelectedInput"
                :name="facetPropertyName"
                type="hidden"
                :value="selectedDate"
            >
            <input
                :id="`@@${ facetPropertyName }`"
                ref="hiddenDefaultInput"
                :name="`@@${ facetPropertyName }`"
                type="hidden"
                :value="formatedStartDate"
            >
        </div>
    </flex-element>
</template>

<script>
import baseOrganism from '@/mixins/base-organism/base-organism-dumb';
import listenOnRootMixin from '@/mixins/listen-on-root';
import { toLocalDate } from '@/utils/date';
import { calendarTheme } from '@/data/constants/calendar-theme';

const CUSTOM_ATTR_NAME = 'search-facet';
const START_DATE_ATTR_NAME = 'calendar-facet-start-date';
const CALENDAR_DATE_HIGHTLIGHT_ATTR_NAME = 'calendar-facet-highlight';
const CALENDAR_DATE_DOT_ATTR_NAME = 'calendar-facet-dot';
const CALENDAR_DATE_BAR_ATTR_NAME = 'calendar-facet-bar';
const CALENDAR_EVENTS_PAGE_ATTR_NAME = 'calendar-facet-event-page-url';
const CALENDAR_TITLE_ATTR_NAME = 'calendar-facet-title';
const CALENDAR_WEEKDAYS_ATTR_NAME = 'calendar-facet-weekdays';
const CALENDAR_DAY_ATTR_NAME = 'calendar-facet-day';

export default {
    mixins: [baseOrganism, listenOnRootMixin],

    data() {
        return {
            facetPropertyName: '',
            selectedDate: '',
            values: [],
            isVisible: true,
            isSelected: false,
            heading: '',
            seeAllVisible: false,
            startDate: new Date(),
            dates: [],
            highlightBgClass: 'f-bg-bc-43',
            highlightTextClass: 'f-bg-bc-43',
            showDots: false,
            showBars: false,
            showHighlight: true,
            dotClass: 'f-bg-bc-43',
            barClass: 'f-bg-bc-43',
            eventPage: '',
            isEventPage: false,
            titleFontClass: '',
            titleFontSizeClass: '',
            titleFontStyleClass: '',
            titleFontWeightClass: '',
            titleColorClass: 'vc-text-gray-800',
            disabledDayClass: '',
            dayContainer: '',
            weekdaysFontColorClass: 'vc-text-gray-500',
            weekdaysFontClass: '',
            weekdaysFontStyleClass: '',
            weekdaysFontSizeClass: 'vc-text-sm',
            weekdaysFontWeightClass: 'vc-font-bold',
            weekdaysBackgroundColorClass: '',
            dayFontColorClass: '',
            dayFontDisabledColor: 'vc-text-gray-400',
            dayFontStyleClass: '',
            dayFontSizeClass: 'vc-font-medium',
            dayFontWeightClass: '',
            dayFontClass: '',
            titleAlignment: '',
            todayHighlightClass: 'f-bg-bc-38',
            formatedStartDate: null,
        };
    },

    computed: {
        attributes() {
            return [
                {
                    dates: this.dates,
                    highlight: this.showHighlight ? { class: this.highlightBgClass, contentClass: this.highlightTextClass } : false,
                    dot: this.showDots ? { class: this.dotClass, style: { backgroundColor: null } } : false,
                    bar: this.showBars ? { class: this.barClass } : false,
                },
                {
                    dates: this.startDate,
                    highlight: { class: this.todayHighlightClass },
                },
            ];
        },

        theme() {
            calendarTheme.color = 'gray';
            calendarTheme.isDark = false;
            calendarTheme.container = {
                light: '',
                dark: '',
            };
            calendarTheme.title.light = `${ this.titleColorClass } ${ this.titleFontClass } ${ this.titleFontWeightClass } ${ this.titleFontStyleClass } ${ this.titleFontSizeClass } hover:vc-opacity-75`;
            calendarTheme.weekdays.light = `${ this.weekdaysFontColorClass } ${ this.weekdaysFontClass } ${ this.weekdaysFontSizeClass } ${ this.weekdaysFontStyleClass } ${ this.weekdaysFontWeightClass } ${ this.weekdaysBackgroundColorClass }`;
            calendarTheme.dayContent = `${ this.dayFontColorClass } ${ this.dayFontStyleClass } ${ this.dayFontSizeClass } ${ this.dayFontWeightClass } ${ this.dayFontClass } vc-cursor-pointer focus:vc-font-bold vc-rounded-full`;
            calendarTheme.dayContentDisabled.light = this.dayFontDisabledColor;
            calendarTheme.bgAccentHigh.light = this.dotClass;
            return calendarTheme;
        },
    },

    mounted() {
        this.listenOnRoot('flex::collection::search-data-facets', this.onQueryUpdate);
        if (this.href) {
            this.eventPage = this.href;
        }
        this.setAttributes();

        const urlParams = new URLSearchParams(window.location.search);
        const parameterName = `@@${ this.facetPropertyName }`;

        if (this.getElementData(START_DATE_ATTR_NAME) && this.getElementData(START_DATE_ATTR_NAME) === 'true') {
            this.startDate = new Date();
            this.formatedStartDate = `>${ this.startDate.toLocaleDateString() }`;
            if (urlParams.get(parameterName)) {
                urlParams.set(parameterName, this.formatedStartDate);
            } else {
                urlParams.append(parameterName, this.formatedStartDate);
            }

            window.history.replaceState({}, '', `${ window.location.pathname }?${ urlParams }`);
            this.emitOnRoot('flex::collection::query-refresh');
        }

        this.isSelected = !!urlParams.get(this.facetPropertyName);
        this.heading = this.element(1) ? this.element(1).singleLineText : this.facetPropertyName;

        this.setEventLink();
    },

    methods: {
        onQueryUpdate(val) {
            const facet = val.find(i => i.name === this.facetPropertyName);
            if (facet && facet.values && facet.values.length > 0) {
                facet.values.forEach((item) => {
                    this.dates.push(toLocalDate(item.value));
                });
                this.isVisible = true;
            } else if (!this.isPageEditor && !this.isTemplateEditor && !this.isComponentEditor) {
                this.isVisible = false;
                this.values = [];
            } else {
                this.values = [];
                this.isVisible = true;
            }
        },

        facetPopulated(visible) {
            this.seeAllVisible = visible;
        },

        setAttributes() {
            this.facetPropertyName = this.getElementData(CUSTOM_ATTR_NAME);

            if (this.hasElementData(CALENDAR_DATE_HIGHTLIGHT_ATTR_NAME)) {
                const highlight = this.getElementData(CALENDAR_DATE_HIGHTLIGHT_ATTR_NAME);
                this.highlightBgClass = highlight && highlight.highlightBgClass !== '' ? highlight.highlightBgClass : this.highlightBgClass;
                this.highlightTextClass = highlight && highlight.highlightTextClass !== '' ? highlight.highlightTextClass : this.highlightTextClass;
                this.showHighlight = highlight ? highlight.showHighlight : this.showHighlight;
            }

            if (this.hasElementData(CALENDAR_DATE_DOT_ATTR_NAME)) {
                const dot = this.getElementData(CALENDAR_DATE_DOT_ATTR_NAME);
                this.dotClass = dot && dot.class !== '' ? dot.class : this.dotClass;
                this.showDots = dot ? dot.show : this.showDots;
            }

            if (this.hasElementData(CALENDAR_DATE_BAR_ATTR_NAME)) {
                const bar = this.getElementData(CALENDAR_DATE_BAR_ATTR_NAME);
                this.barClass = bar && bar.class !== '' ? bar.class : this.barClass;
                this.showBars = bar ? bar.show : this.showBars;
            }

            if (this.hasElementData(CALENDAR_EVENTS_PAGE_ATTR_NAME)) {
                this.isEventPage = this.getElementData(CALENDAR_EVENTS_PAGE_ATTR_NAME);
            }

            if (this.eventPage && this.isEventPage) {
                const closestForm = this.$el.closest('form');
                if (closestForm) {
                    closestForm.action = this.eventPage;
                }
            }

            if (this.hasElementData(CALENDAR_TITLE_ATTR_NAME)) {
                const title = this.getElementData(CALENDAR_TITLE_ATTR_NAME);
                this.titleFontClass = title ? title.font : this.titleFontClass;
                this.titleFontSizeClass = title ? title.size : this.titleFontSizeClass;
                this.titleFontStyleClass = title ? title.style : this.titleFontStyleClass;
                this.titleFontWeightClass = title ? title.weight : this.titleFontWeightClass;
                this.titleColorClass = title ? title.color : this.titleColorClass;
                this.titleAlignment = title ? title.alignment : this.titleAlignment;
            }

            if (this.hasElementData(CALENDAR_WEEKDAYS_ATTR_NAME)) {
                const weekdays = this.getElementData(CALENDAR_WEEKDAYS_ATTR_NAME);
                this.weekdaysFontColorClass = weekdays ? weekdays.color : this.weekdaysFontColorClass;
                this.weekdaysFontClass = weekdays ? weekdays.font : this.weekdaysFontClass;
                this.weekdaysFontSizeClass = weekdays ? weekdays.size : this.weekdaysFontSizeClass;
                this.weekdaysFontStyleClass = weekdays ? weekdays.style : this.weekdaysFontStyleClass;
                this.weekdaysFontWeightClass = weekdays ? weekdays.weight : this.weekdaysFontWeightClass;
                this.weekdaysBackgroundColorClass = weekdays ? weekdays.backgroundColor : this.weekdaysBackgroundColorClass;
            }

            if (this.hasElementData(CALENDAR_DAY_ATTR_NAME)) {
                const dayContent = this.getElementData(CALENDAR_DAY_ATTR_NAME);
                this.dayFontColorClass = dayContent ? dayContent.color : this.dayFontColorClass;
                this.dayFontDisabledColor = dayContent ? dayContent.disabledColor : this.dayFontDisabledColor;
                this.dayFontStyleClass = dayContent ? dayContent.style : this.dayFontStyleClass;
                this.dayFontSizeClass = dayContent ? dayContent.size : this.dayFontSizeClass;
                this.dayFontWeightClass = dayContent ? dayContent.weight : this.dayFontWeightClass;
                this.dayFontClass = dayContent ? dayContent.font : this.dayFontClass;
            }
        },

        dayClicked(event) {
            const startDate = new Date(event.date);
            const endDate = new Date(event.date);
            endDate.setDate(startDate.getDate() + 1);

            // Expecting two digit - Day / Month
            const startDateDay = startDate.getDate().toLocaleString('en-US', { minimumIntegerDigits: 2 });
            const startDateMonth = (startDate.getMonth() + 1).toLocaleString('en-US', { minimumIntegerDigits: 2 });
            const endDateDay = endDate.getDate().toLocaleString('en-US', { minimumIntegerDigits: 2 });
            const endDateMonth = (endDate.getMonth() + 1).toLocaleString('en-US', { minimumIntegerDigits: 2 });

            if (startDate) {
                this.selectedDate = `[${ startDate.getFullYear() }-${ startDateMonth }-${ startDateDay }T00:00:00Z TO ${ endDate.getFullYear() }-${ endDateMonth }-${ endDateDay }T00:00:00Z]`;
                this.$refs.hiddenSelectedInput.value = this.selectedDate;

                if (this.eventPage && this.isEventPage) {
                    const closestForm = this.$el.closest('form');
                    if (closestForm) {
                        closestForm.submit();
                        return;
                    }
                }

                this.$emit('submit-form');
            }
        },

        setEventLink() {
            if (this.isEventPage) {
                let href = this.href;
                let anchor = this.anchor;
                if (href.includes('#')) {
                    [href, anchor] = href.split('#');
                }

                this.hrefNew = href;
                this.anchorNew = anchor;
                this.linkTypeNew = this.linkType;
                this.titleNew = this.title;
                this.targetNew = this.target;
                this.queryStringNew = this.queryString;
                this.mediaIdNew = this.mediaId;
            }
        },
    },
};
</script>
